// MYEVBUILDS global Amazon-style UK delivery countdown.
(() => {
  "use strict";

  const RELEASE = "1.10.6";
  if (window.__MYEV_DELIVERY_ESTIMATE__?.version === RELEASE) {
    window.__MYEV_DELIVERY_ESTIMATE__.refresh();
    return;
  }

  const CONFIG = Object.freeze({
    timeZone: "Europe/London",
    deliveryCutoffMinute: (18 * 60) + 30,
    midnightCutoffMinute: 24 * 60,
    clockRefreshMs: 5 * 60 * 1000,
    displayRefreshMs: 1000,
  });

  const ukClockFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: CONFIG.timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const weekdayLongFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "long",
  });
  const weekdayShortFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    weekday: "short",
  });
  const monthLongFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "long",
  });
  const monthShortFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    month: "short",
  });

  let serverOffsetMs = 0;
  let clockSource = "device";
  let lastClockSync = 0;
  let clockSyncPromise = null;
  let renderQueued = false;

  function partsObject(formatter, date) {
    return formatter.formatToParts(date).reduce((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
  }

  function londonParts(timestamp = Date.now() + serverOffsetMs) {
    const parts = partsObject(ukClockFormatter, new Date(timestamp));
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
    };
  }

  function londonNow() {
    return calculationClock(londonParts());
  }

  function calculationClock(parts) {
    return {
      ...parts,
      calendarDate: new Date(Date.UTC(parts.year, parts.month - 1, parts.day)),
      secondsSinceMidnight:
        (parts.hour * 60 * 60) + (parts.minute * 60) + parts.second,
    };
  }

  function addCalendarDays(date, numberOfDays) {
    const result = new Date(date.getTime());
    result.setUTCDate(result.getUTCDate() + numberOfDays);
    return result;
  }

  const UK_BANK_HOLIDAYS = new Set([
    "2026-01-01",
    "2026-04-03",
    "2026-04-06",
    "2026-05-04",
    "2026-05-25",
    "2026-08-31",
    "2026-12-25",
    "2026-12-28",
    "2027-01-01",
    "2027-03-26",
    "2027-03-29",
    "2027-05-03",
    "2027-05-31",
    "2027-08-30",
    "2027-12-27",
    "2027-12-28",
  ]);

  function calendarKey(date) {
    return date.toISOString().slice(0, 10);
  }

  function isBankHoliday(date) {
    return UK_BANK_HOLIDAYS.has(calendarKey(date));
  }

  function isDispatchDay(date) {
    return date.getUTCDay() !== 0 && !isBankHoliday(date);
  }

  function isDeliveryDay(date) {
    return !isBankHoliday(date);
  }

  function addMatchingDays(date, numberOfDays, isCountableDay) {
    const result = new Date(date.getTime());
    let remaining = numberOfDays;
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (isCountableDay(result)) remaining -= 1;
    }
    return result;
  }

  function addDispatchDays(date, numberOfDays) {
    return addMatchingDays(date, numberOfDays, isDispatchDay);
  }

  function addDeliveryDays(date, numberOfDays) {
    return addMatchingDays(date, numberOfDays, isDeliveryDay);
  }

  function isSameCalendarDay(left, right) {
    return calendarKey(left) === calendarKey(right);
  }

  function ordinal(day) {
    const lastTwoDigits = day % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return "th";
    if (day % 10 === 1) return "st";
    if (day % 10 === 2) return "nd";
    if (day % 10 === 3) return "rd";
    return "th";
  }

  function compactDate(date) {
    return `${weekdayShortFormatter.format(date)} ${monthShortFormatter.format(date)} ${date.getUTCDate()}`;
  }

  function fullDate(date) {
    const day = date.getUTCDate();
    return `${weekdayLongFormatter.format(date)} ${monthLongFormatter.format(date)} ${day}${ordinal(day)}`;
  }

  function countdownPresentation(secondsRemaining) {
    const totalSeconds = Math.floor(Math.max(0, secondsRemaining));
    if (totalSeconds < 60 * 60) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return {
        text:
          `${minutes} ${minutes === 1 ? "min" : "mins"} `
          + `${seconds} ${seconds === 1 ? "sec" : "secs"}`,
        tone: "red",
      };
    }

    const totalMinutes = Math.floor(totalSeconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hr" : "hrs"}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? "min" : "mins"}`);
    return {
      text: parts.join(" "),
      tone: totalSeconds < 3 * 60 * 60 ? "orange" : "green",
    };
  }

  function deliveryEstimateForClock(now) {
    const currentSeconds = now.secondsSinceMidnight;
    const deliveryCutoffSeconds = CONFIG.deliveryCutoffMinute * 60;
    const midnightCutoffSeconds = CONFIG.midnightCutoffMinute * 60;

    let labelText;
    let targetSeconds;
    let promiseText;
    const canDispatchToday = isDispatchDay(now.calendarDate)
      && currentSeconds <= deliveryCutoffSeconds;

    if (canDispatchToday) {
      labelText = "NEXT DAY DELIVERY";
      targetSeconds = deliveryCutoffSeconds;
      promiseText = "for next day delivery";
    } else {
      labelText = "2 DAY DELIVERY";
      targetSeconds = midnightCutoffSeconds;
      promiseText = "for delivery";
    }

    const dispatchDate = canDispatchToday
      ? now.calendarDate
      : addDispatchDays(now.calendarDate, 1);
    const deliveryDates = [addDeliveryDays(dispatchDate, 1)];
    const tomorrowFirst = isSameCalendarDay(
      deliveryDates[0],
      addCalendarDays(now.calendarDate, 1),
    );
    const countdown = countdownPresentation(targetSeconds - currentSeconds);
    const remainingText = countdown.text;
    const compactDates = deliveryDates.map(compactDate).join(" / ");
    const fullDates = deliveryDates.map(fullDate).join(" / ");

    return {
      labelText,
      remainingText,
      countdownTone: countdown.tone,
      promiseText,
      compactDates,
      fullDates,
      tomorrowFirst,
      cardText:
        `Order within the next ${remainingText} ${promiseText} ${compactDates}`,
      productCountdownText:
        `Order within the next ${remainingText} ${promiseText}`,
      signature:
        `${labelText}|${remainingText}|${promiseText}|${compactDates}|${fullDates}`,
    };
  }

  function deliveryEstimate() {
    return deliveryEstimateForClock(londonNow());
  }

  function replaceWithParts(element, parts) {
    const expectedText = parts.map((part) => (
      typeof part === "string" ? part : part.text
    )).join("");
    const hasExpectedClasses = parts
      .filter((part) => typeof part !== "string")
      .every((part) => part.className.split(/\s+/).every((className) => (
        element.querySelector(`.${className}`)
      )));
    if (element.textContent === expectedText && hasExpectedClasses) return;

    const fragment = document.createDocumentFragment();
    for (const part of parts) {
      if (typeof part === "string") {
        fragment.append(document.createTextNode(part));
      } else {
        const span = document.createElement("span");
        span.className = part.className;
        span.textContent = part.text;
        fragment.append(span);
      }
    }
    element.replaceChildren(fragment);
  }

  function renderCard(dateElement, estimate) {
    replaceWithParts(dateElement, [
      "Order within the next ",
      {
        className:
          `myev-delivery__countdown myev-delivery__countdown--${estimate.countdownTone}`,
        text: estimate.remainingText,
      },
      ` ${estimate.promiseText} ${estimate.compactDates}`,
    ]);
  }

  function renderProduct(dateElement, detailElement, estimate) {
    const dateParts = [];
    if (estimate.tomorrowFirst) {
      dateParts.push({
        className: "myev-delivery__tomorrow",
        text: "Tomorrow",
      });
      dateParts.push(" ");
    }
    dateParts.push(estimate.fullDates);
    replaceWithParts(dateElement, dateParts);

    const detailParts = [
      "Order within the next ",
      {
        className:
          `myev-delivery__countdown myev-delivery__countdown--${estimate.countdownTone}`,
        text: estimate.remainingText,
      },
      ` ${estimate.promiseText}`,
    ];
    if (!estimate.tomorrowFirst) {
      detailParts.push(` ${estimate.fullDates}`);
    }
    replaceWithParts(detailElement, detailParts);
  }

  function renderElement(element, estimate) {
    const labelElement = element.querySelector("[data-myev-delivery-label]");
    const dateElement = element.querySelector("[data-myev-delivery-date]");
    const detailElement = element.querySelector("[data-myev-delivery-detail]");
    if (!labelElement || !dateElement || !detailElement) return;

    const mode = element.dataset.myevDeliveryMode || "product";
    const signature = `${mode}|${estimate.signature}`;
    if (labelElement.textContent !== estimate.labelText) {
      labelElement.textContent = estimate.labelText;
    }
    if (mode === "card") {
      renderCard(dateElement, estimate);
    } else {
      renderProduct(dateElement, detailElement, estimate);
    }

    element.dataset.myevDeliverySignature = signature;
    element.dataset.myevDeliveryOwner = RELEASE;
    element.dataset.myevClockSource = clockSource;
    element.classList.add("myev-delivery--ready");
  }

  function renderAll() {
    renderQueued = false;
    const estimate = deliveryEstimate();
    document
      .querySelectorAll("[data-myev-delivery-estimate]")
      .forEach((element) => renderElement(element, estimate));
  }

  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    window.requestAnimationFrame(renderAll);
  }

  async function syncShopifyClock(force = false) {
    const now = Date.now();
    if (!force && now - lastClockSync < CONFIG.clockRefreshMs) return;
    if (clockSyncPromise) return clockSyncPromise;

    clockSyncPromise = (async () => {
      try {
        const root = window.Shopify?.routes?.root || "/";
        const separator = root.includes("?") ? "&" : "?";
        const response = await fetch(
          `${root}cart.js${separator}myev_delivery_clock=${now}`,
          {
            method: "GET",
            credentials: "same-origin",
            cache: "no-store",
            headers: { Accept: "application/json" },
          },
        );
        if (!response.ok) throw new Error("Shopify clock request failed");

        const serverTimestamp = Date.parse(response.headers.get("date") || "");
        if (!Number.isFinite(serverTimestamp)) {
          throw new Error("Shopify clock header unavailable");
        }
        if (Math.abs(serverTimestamp - Date.now()) > 24 * 60 * 60 * 1000) {
          throw new Error("Shopify clock header was outside the safe range");
        }

        serverOffsetMs = serverTimestamp - Date.now();
        clockSource = "shopify";
        lastClockSync = Date.now();
      } catch {
        clockSource = "device";
        lastClockSync = Date.now();
      } finally {
        clockSyncPromise = null;
        queueRender();
      }
    })();

    return clockSyncPromise;
  }

  function start() {
    renderAll();
    syncShopifyClock(true);
    new MutationObserver(queueRender).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    window.setInterval(queueRender, CONFIG.displayRefreshMs);
    window.setInterval(() => syncShopifyClock(), CONFIG.clockRefreshMs);
    document.addEventListener("shopify:section:load", queueRender);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") syncShopifyClock(true);
    });
  }

  window.__MYEV_DELIVERY_ESTIMATE__ = {
    version: RELEASE,
    refresh: queueRender,
    syncClock: () => syncShopifyClock(true),
    calculate: deliveryEstimate,
    calculateForParts: (parts) => (
      deliveryEstimateForClock(calculationClock(parts))
    ),
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
